import {useCallback} from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';
import {immer} from 'zustand/middleware/immer';
import {devtools} from 'zustand/middleware';

interface BookExperienceState {
  itemQuantities: {
    [offerId: string]: {
      [itemId: string]: number;
    };
  };
  email: string;
}

interface BookExperienceActions {
  setEmail: (email: string) => void;
  setItemQuantity: (payload: {offerId: string; itemId: string; quantity: number}) => void;
  resetItemQuantities: (offerId: string) => void;
}

interface BookExperienceStore extends BookExperienceState, BookExperienceActions {}

const INITIAL_STATE: BookExperienceState = {
  itemQuantities: {},
  email: '',
};

export const useBookExperienceStore = create<BookExperienceStore>()(
  devtools(
    immer(set => ({
      ...INITIAL_STATE,

      setEmail: email =>
        set(state => {
          state.email = email;
        }),

      setItemQuantity: ({offerId, itemId, quantity}) =>
        set(state => {
          if (!state.itemQuantities[offerId]) {
            state.itemQuantities[offerId] = {};
          }
          state.itemQuantities[offerId][itemId] = quantity;
        }),

      resetItemQuantities: offerId =>
        set(state => {
          state.itemQuantities[offerId] = {};
        }),
    })),
    {name: 'BookExperienceStore'},
  ),
);

export const selectEmail = (state: BookExperienceStore) => state.email;

export const useItemQuantitiesByOfferId = (offerId?: string) => {
  const selector = useCallback(
    (state: BookExperienceStore) => (offerId ? state.itemQuantities[offerId] : undefined),
    [offerId],
  );
  return useBookExperienceStore(useShallow(selector)) ?? {};
};
